import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ahobilam.com';
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

export const ComponentBuilder = ({ onInsertComponent, onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [customTitle, setCustomTitle] = useState('');
  const [customText, setCustomText] = useState('');
  const [itemsList, setItemsList] = useState([
    { title: 'Section 1', content: 'Detailed explanation text...' },
    { title: 'Section 2', content: 'Additional details...' }
  ]);

  const COMPONENT_TYPES = [
    { id: 'card', name: '🎴 Parchment Card', desc: 'Single content card with golden border' },
    { id: 'accordion', name: '📂 Sastra Accordion', desc: 'Expandable/collapsible Q&A list' },
    { id: 'tabs', name: '📑 Multi-Tab View', desc: 'Tabbed switcher for morning/evening rituals' },
    { id: 'dropdown', name: '📋 Selection Dropdown', desc: 'Interactive dropdown menu list' },
    { id: 'table', name: '📅 Sastra Data Table', desc: 'Structured comparative table' },
    { id: 'button', name: '🔘 CTA Gold Button', desc: 'Action button with icon' },
    { id: 'form', name: '📝 Feedback / Inquiry Form', desc: 'Interactive member input form' },
  ];

  const handleAddItem = () => {
    setItemsList([...itemsList, { title: `Section ${itemsList.length + 1}`, content: 'Enter text here...' }]);
  };

  const handleUpdateItem = (index, field, value) => {
    const updated = [...itemsList];
    updated[index][field] = value;
    setItemsList(updated);
  };

  const handleRemoveItem = (index) => {
    setItemsList(itemsList.filter((_, i) => i !== index));
  };

  const handleGenerate = (type) => {
    const payload = {
      id: `comp_${Date.now()}`,
      type: type,
      title: customTitle || 'New Component',
      text: customText,
      items: itemsList,
      created_at: new Date().toISOString()
    };
    onInsertComponent(payload);
  };

  return (
    <div className="bg-white p-5 rounded-2xl border-2 border-[#D4AF37] shadow-xl text-xs text-[#1B5E20]">
      <div className="flex justify-between items-center pb-3 mb-4 border-b border-[#D4AF37]/30">
        <h3 className="text-sm font-extrabold text-[#1A237E] flex items-center gap-2">
          <span>🧩</span> UI Component Generator & Inserter
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 font-bold text-base">✕</button>
      </div>

      {/* Component Type Selector */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        {COMPONENT_TYPES.map((comp) => (
          <button
            key={comp.id}
            onClick={() => setSelectedCategory(comp.id)}
            className={`p-2.5 rounded-xl border text-left transition-all ${
              selectedCategory === comp.id
                ? 'bg-[#CC6600] text-white border-[#CC6600] shadow-xs'
                : 'bg-[#FAF6EE] text-[#1B5E20] border-[#D4AF37]/30 hover:bg-[#FDF8ED]'
            }`}
          >
            <div className="font-bold text-xs">{comp.name}</div>
            <div className="text-[10px] opacity-80 mt-0.5 line-clamp-1">{comp.desc}</div>
          </button>
        ))}
      </div>

      {/* Configuration Form */}
      <div className="space-y-3 bg-[#FAF6EE] p-4 rounded-xl border border-[#D4AF37]/30 mb-4">
        <div>
          <label className="block font-bold text-[#1A237E] mb-1">Title / Header</label>
          <input
            type="text"
            value={customTitle}
            onChange={(e) => setCustomTitle(e.target.value)}
            placeholder="e.g. Daily Sandhyavandanam Steps"
            className="w-full p-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-[#CC6600]"
          />
        </div>

        <div>
          <label className="block font-bold text-[#1A237E] mb-1">Description / Introductory Text</label>
          <textarea
            rows="2"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            placeholder="Enter overview text..."
            className="w-full p-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-[#CC6600]"
          />
        </div>

        {/* Dynamic Items List (for Accordions, Tabs, Tables) */}
        {(selectedCategory === 'accordion' || selectedCategory === 'tabs' || selectedCategory === 'table') && (
          <div className="space-y-2 pt-2 border-t border-[#D4AF37]/30">
            <div className="flex justify-between items-center">
              <label className="font-bold text-[#1A237E]">Sections / Items ({itemsList.length})</label>
              <button
                onClick={handleAddItem}
                className="px-2.5 py-1 bg-[#1B5E20] text-white text-[11px] font-bold rounded-md hover:bg-[#2D5A1E]"
              >
                + Add Item
              </button>
            </div>

            {itemsList.map((item, idx) => (
              <div key={idx} className="flex gap-2 items-center bg-white p-2 rounded-lg border border-gray-200">
                <input
                  type="text"
                  value={item.title}
                  onChange={(e) => handleUpdateItem(idx, 'title', e.target.value)}
                  placeholder="Section title"
                  className="w-1/3 p-1.5 border border-gray-200 rounded text-xs"
                />
                <input
                  type="text"
                  value={item.content}
                  onChange={(e) => handleUpdateItem(idx, 'content', e.target.value)}
                  placeholder="Content text"
                  className="flex-1 p-1.5 border border-gray-200 rounded text-xs"
                />
                <button
                  onClick={() => handleRemoveItem(idx)}
                  className="text-red-600 font-bold px-1.5 hover:bg-red-50 rounded"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Insert Button */}
      <button
        onClick={() => handleGenerate(selectedCategory === 'all' ? 'card' : selectedCategory)}
        className="w-full py-2.5 bg-gradient-to-r from-[#CC6600] to-[#E65100] hover:from-[#E65100] hover:to-[#CC6600] text-white font-bold rounded-xl shadow-md transition-all text-xs"
      >
        ✨ Insert Component Into Page
      </button>
    </div>
  );
};

export default ComponentBuilder;
